import React, { ReactElement } from 'react'
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { FloridaApplicationPacket } from 'src/v3/content-pipeline/accreditation/utils/florida-application-api'

let styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#1a1a1a',
  },
  title: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6,
  },
  meta: {
    fontSize: 10,
    marginBottom: 14,
    lineHeight: 1.4,
  },
  sectionHeader: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginTop: 12,
    marginBottom: 4,
  },
  paragraph: {
    fontSize: 10,
    lineHeight: 1.4,
  },
  listItem: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #999',
    paddingBottom: 3,
    marginTop: 4,
    marginBottom: 3,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '0.5pt solid #ddd',
    paddingVertical: 3,
  },
  tableHeaderCell: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
  },
  tableCell: {
    fontSize: 9,
  },
})

let MISSING_REQUIREMENT_LABELS: Record<
  FloridaApplicationPacket['missingRequirements'][number],
  string
> = {
  description: 'Course description',
  objectives: 'Learning objectives',
  instructors: 'Instructor assignment',
  instructorCredentials: 'Instructor credentials',
  references: 'References',
  outline: 'Course outline / agenda',
  teachingStrategies: 'Teaching strategies / learner engagement',
  assessment: 'Post-test / assessment',
  capceApproval: 'Finalized CAPCE approval',
  contactHourMinimum: 'Minimum contact-hour length',
}

function formatOutlineTiming(
  row: FloridaApplicationPacket['outline'][number],
): string {
  if (row.startTime && row.endTime) {
    return `${row.startTime.replace('T', ' ')} – ${row.endTime.replace('T', ' ')}${
      row.timeZone ? ` (${row.timeZone})` : ''
    }`
  }
  if (row.durationMinutes !== undefined) return `${row.durationMinutes} min`
  return '—'
}

export default function FloridaApplicationPdf({
  packet,
}: {
  packet: FloridaApplicationPacket
}): ReactElement {
  let meta = [
    `Provider: ${packet.provider}`,
    `CE Hours: ${packet.contactHours.creditedContactHours}`,
    `Delivery: ${packet.delivery.label}`,
    packet.capce?.courseCode && `CAPCE Course No: ${packet.capce.courseCode}`,
    (packet.capce?.topicArea || packet.category) &&
      `Topic Area: ${packet.capce?.topicArea ?? packet.category}`,
  ]
    .filter(Boolean)
    .join(' | ')

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <Text style={styles.title}>{packet.displayTitle}</Text>
        <Text style={styles.meta}>{meta}</Text>

        {!packet.contactHours.meetsMinimumLength && (
          <Text
            style={{ ...styles.paragraph, color: '#b00020', marginBottom: 10 }}
          >
            This offering is under Florida's 50-minute minimum and does not
            qualify for contact-hour credit as scheduled.
          </Text>
        )}

        {packet.description && (
          <>
            <Text style={styles.sectionHeader}>Course Description</Text>
            <Text style={styles.paragraph}>{packet.description}</Text>
          </>
        )}

        {packet.practiceGap && (
          <>
            <Text style={styles.sectionHeader}>Practice Gap</Text>
            <Text style={styles.paragraph}>{packet.practiceGap}</Text>
          </>
        )}

        {packet.practiceGapEvidence && (
          <>
            <Text style={styles.sectionHeader}>Practice Gap Evidence</Text>
            <Text style={styles.paragraph}>{packet.practiceGapEvidence}</Text>
          </>
        )}

        {packet.objectives.length > 0 && (
          <>
            <Text style={styles.sectionHeader}>Learning Objectives</Text>
            {packet.objectives.map((o) => (
              <Text key={o.sequence} style={styles.listItem}>
                {o.sequence}. {o.text}
              </Text>
            ))}
          </>
        )}

        {packet.learningOutcomes && (
          <>
            <Text style={styles.sectionHeader}>Learning Outcomes</Text>
            <Text style={styles.paragraph}>{packet.learningOutcomes}</Text>
          </>
        )}

        {packet.learnerEngagement && (
          <>
            <Text style={styles.sectionHeader}>
              Teaching Strategies &amp; Learner Engagement
            </Text>
            <Text style={styles.paragraph}>{packet.learnerEngagement}</Text>
          </>
        )}

        {packet.instructors.length > 0 && (
          <>
            <Text style={styles.sectionHeader}>
              {packet.instructors.length === 1 ? 'Instructor' : 'Instructors'}
            </Text>
            {packet.instructors.map((i) => (
              <Text key={i.id} style={styles.listItem}>
                {i.name}
                {i.credentials ? `, ${i.credentials}` : ''}
              </Text>
            ))}
          </>
        )}

        {packet.references.length > 0 && (
          <>
            <Text style={styles.sectionHeader}>References</Text>
            {packet.references.map((r) => (
              <Text key={r.sequence} style={styles.listItem}>
                - {r.text}
              </Text>
            ))}
          </>
        )}

        {packet.outline.length > 0 && (
          <>
            <Text style={styles.sectionHeader}>Course Outline / Agenda</Text>
            <View style={styles.tableHeaderRow}>
              <Text style={{ ...styles.tableHeaderCell, width: '10%' }}>#</Text>
              <Text style={{ ...styles.tableHeaderCell, width: '55%' }}>
                Content
              </Text>
              <Text style={{ ...styles.tableHeaderCell, width: '35%' }}>
                Timing
              </Text>
            </View>
            {packet.outline.map((row) => (
              <View key={row.sequence} style={styles.tableRow}>
                <Text style={{ ...styles.tableCell, width: '10%' }}>
                  {row.sequence}
                </Text>
                <Text style={{ ...styles.tableCell, width: '55%' }}>
                  {row.title ?? '(untitled)'}
                </Text>
                <Text style={{ ...styles.tableCell, width: '35%' }}>
                  {formatOutlineTiming(row)}
                </Text>
              </View>
            ))}
          </>
        )}

        {packet.missingRequirements.length > 0 && (
          <>
            <Text style={{ ...styles.sectionHeader, color: '#b00020' }}>
              Open Gaps Before Submission
            </Text>
            {packet.missingRequirements.map((req) => (
              <Text key={req} style={{ ...styles.listItem, color: '#b00020' }}>
                - {MISSING_REQUIREMENT_LABELS[req] ?? req}
              </Text>
            ))}
          </>
        )}
      </Page>
    </Document>
  )
}
